// captureParse.js — deterministic natural-language parser for Smart Capture.
//
// The host types a plan in their own words ("send menu selections to Venue Mon
// Jul 29 12PM"). This turns that into an editable draft WITHOUT inventing a
// vendor task or discarding the date they typed:
//   • If an explicit date/time is present, USE IT verbatim (no lead-time math).
//   • The title is the host's own words, cleaned + capitalized — never renamed
//     to "Confirm caterer". It is a SUGGESTION the host can edit before saving.
//   • Only when NO date is given do we fall back to the category lead-time safe
//     start (the original useful behavior for "book a videographer").
//
// Pure functions only — no React, no network, no mutation. Unit-tested in
// __tests__/captureParse.test.js.

import { categorize, leadDaysFor, lastSafeStart } from './calendarRunway';

const DAY = 86400000;

const MONTHS = {
  jan: 0, january: 0, feb: 1, february: 1, mar: 2, march: 2, apr: 3, april: 3,
  may: 4, jun: 5, june: 5, jul: 6, july: 6, aug: 7, august: 7,
  sep: 8, sept: 8, september: 8, oct: 9, october: 9, nov: 10, november: 10,
  dec: 11, december: 11,
};

const WEEKDAYS = {
  sun: 0, sunday: 0, mon: 1, monday: 1, tue: 2, tues: 2, tuesday: 2,
  wed: 3, weds: 3, wednesday: 3, thu: 4, thur: 4, thurs: 4, thursday: 4,
  fri: 5, friday: 5, sat: 6, saturday: 6,
};

// Connector / filler words that should not survive into a title once the date
// they introduced is stripped out ("send X **on** Jul 29" -> "send X").
const FILLER = new Set(['on', 'at', 'by', 'for', 'the', 'this', 'next', 'a', 'of', 'to-do', 'todo']);

function ymd(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function startOfDay(d) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

// Given a month/day with no year, pick the year that keeps the date in the
// future (this year, or next year if it has already passed).
function inferYear(month, day, today) {
  const t = startOfDay(today);
  let y = t.getFullYear();
  let cand = new Date(y, month, day);
  if (startOfDay(cand) < t) cand = new Date(y + 1, month, day);
  return cand;
}

function pad2(n) { return String(n).padStart(2, '0'); }

// ── time ── only commit to a time when the host is explicit (am/pm, noon,
// midnight, or "at H[:MM]"). Returns { time:'HH:MM', matched:'...' } or null.
function parseTime(text) {
  const lower = text.toLowerCase();

  let m = /\bnoon\b/.exec(lower);
  if (m) return { time: '12:00', matched: m[0] };
  m = /\bmidnight\b/.exec(lower);
  if (m) return { time: '00:00', matched: m[0] };

  // 12pm · 12:30 pm · 7 p.m.
  m = /\b(\d{1,2})(?::(\d{2}))?\s*([ap])\.?\s*m\.?\b/.exec(lower);
  if (m) {
    let h = parseInt(m[1], 10);
    const min = m[2] ? parseInt(m[2], 10) : 0;
    if (h < 1 || h > 12 || min > 59) return null;
    if (m[3] === 'p' && h !== 12) h += 12;
    if (m[3] === 'a' && h === 12) h = 0;
    return { time: `${pad2(h)}:${pad2(min)}`, matched: m[0] };
  }

  // "at 3" / "at 9:30" — assume a sensible daytime hour (1–6 -> pm, 7–11 -> am, 12 -> noon).
  m = /\bat\s+(\d{1,2})(?::(\d{2}))?\b/.exec(lower);
  if (m) {
    let h = parseInt(m[1], 10);
    const min = m[2] ? parseInt(m[2], 10) : 0;
    if (h < 1 || h > 23 || min > 59) return null;
    if (h <= 6) h += 12;            // 1–6 -> afternoon
    return { time: `${pad2(h)}:${pad2(min)}`, matched: m[1] + (m[2] ? `:${m[2]}` : '') };
  }
  return null;
}

// ── date ── tries explicit forms first, then relative, then weekday.
// Returns { date: Date, matched: [strings] } or null.
function parseDate(text, today) {
  const lower = text.toLowerCase();
  const t = startOfDay(today);

  // 7/29 · 07-29-2026 · 7.29
  let m = /\b(\d{1,2})[/\-.](\d{1,2})(?:[/\-.](\d{2,4}))?\b/.exec(lower);
  if (m) {
    const mo = parseInt(m[1], 10) - 1;
    const day = parseInt(m[2], 10);
    if (mo >= 0 && mo <= 11 && day >= 1 && day <= 31) {
      let date;
      if (m[3]) {
        let y = parseInt(m[3], 10);
        if (y < 100) y += 2000;
        date = new Date(y, mo, day);
      } else {
        date = inferYear(mo, day, t);
      }
      return { date, matched: [m[0]] };
    }
  }

  // july 29 · jul 29, 2026 · jul. 29
  m = /\b(jan|january|feb|february|mar|march|apr|april|may|jun|june|jul|july|aug|august|sep|sept|september|oct|october|nov|november|dec|december)\.?\s+(\d{1,2})(?:st|nd|rd|th)?(?:,?\s+(\d{4}))?\b/.exec(lower);
  if (m) {
    const mo = MONTHS[m[1]];
    const day = parseInt(m[2], 10);
    if (day >= 1 && day <= 31) {
      const date = m[3] ? new Date(parseInt(m[3], 10), mo, day) : inferYear(mo, day, t);
      return { date, matched: [m[0]] };
    }
  }

  // 29 july · 29th of july
  m = /\b(\d{1,2})(?:st|nd|rd|th)?\s+(?:of\s+)?(jan|january|feb|february|mar|march|apr|april|may|jun|june|jul|july|aug|august|sep|sept|september|oct|october|nov|november|dec|december)\b/.exec(lower);
  if (m) {
    const mo = MONTHS[m[2]];
    const day = parseInt(m[1], 10);
    if (day >= 1 && day <= 31) return { date: inferYear(mo, day, t), matched: [m[0]] };
  }

  // today · tonight · tomorrow
  if (/\btoday\b/.test(lower) || /\btonight\b/.test(lower)) {
    return { date: new Date(t), matched: [/\btonight\b/.test(lower) ? 'tonight' : 'today'] };
  }
  if (/\btomorrow\b/.test(lower)) {
    return { date: new Date(t.getTime() + DAY), matched: ['tomorrow'] };
  }

  // in 3 days · in 2 weeks · in 1 month
  m = /\bin\s+(\d{1,3})\s+(day|days|week|weeks|month|months)\b/.exec(lower);
  if (m) {
    const n = parseInt(m[1], 10);
    const unit = m[2];
    let date;
    if (unit.startsWith('day')) date = new Date(t.getTime() + n * DAY);
    else if (unit.startsWith('week')) date = new Date(t.getTime() + n * 7 * DAY);
    else date = new Date(t.getFullYear(), t.getMonth() + n, t.getDate());
    return { date, matched: [m[0]] };
  }

  // next week
  if (/\bnext\s+week\b/.test(lower)) {
    return { date: new Date(t.getTime() + 7 * DAY), matched: ['next week'] };
  }

  // [this|next] monday · fri · saturday  -> next occurrence of that weekday
  m = /\b(this|next)?\s*(sun|sunday|mon|monday|tue|tues|tuesday|wed|weds|wednesday|thu|thur|thurs|thursday|fri|friday|sat|saturday)\b/.exec(lower);
  if (m && (m[1] || WEEKDAYS[m[2]] != null)) {
    const target = WEEKDAYS[m[2]];
    let delta = (target - t.getDay() + 7) % 7;
    if (delta === 0) delta = 7;            // "monday" on a Monday = the next one
    if (m[1] === 'next') delta += 7;
    const date = new Date(t.getTime() + delta * DAY);
    return { date, matched: [m[0].trim()] };
  }

  return null;
}

// Strip the matched date/time fragments + dangling filler, collapse, capitalize.
function buildTitle(original, fragments) {
  let s = ` ${original} `;
  fragments.filter(Boolean).forEach((frag) => {
    const re = new RegExp(frag.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'ig');
    s = s.replace(re, ' ');
  });
  const words = s.split(/\s+/).filter(Boolean);
  // drop trailing/leading filler left behind once the date is gone
  while (words.length && FILLER.has(words[0].toLowerCase())) words.shift();
  while (words.length && FILLER.has(words[words.length - 1].toLowerCase())) words.pop();
  let title = words.join(' ').replace(/\s+([,.;:!?])/g, '$1').trim();
  title = title.replace(/[\s,;:-]+$/, '').replace(/^[\s,;:-]+/, '');
  if (!title) return '';
  return title.charAt(0).toUpperCase() + title.slice(1);
}

/**
 * parseCapture(text, { today, eventDate }) -> editable draft.
 * {
 *   title,            // host's words, cleaned + capitalized (editable suggestion)
 *   detail,           // original text, for the calendar note/description
 *   date,             // 'YYYY-MM-DD'
 *   time,             // 'HH:MM' | null
 *   hasExplicitDate,  // true when the host typed a date
 *   category, lead,   // lead-time reasoning, used only when no explicit date
 * }
 */
export function parseCapture(text, { today = new Date(), eventDate = null } = {}) {
  const original = String(text || '').trim();
  if (!original) return null;
  const t = startOfDay(today);

  const dateHit = parseDate(original, t);
  const timeHit = parseTime(original);

  const fragments = [];
  if (dateHit) fragments.push(...dateHit.matched);
  if (timeHit) fragments.push(timeHit.matched);

  let title = buildTitle(original, fragments);
  if (!title) title = buildTitle(original, []) || 'Reminder';

  const category = categorize(original);
  const lead = leadDaysFor(category);

  let date;
  let hasExplicitDate = false;
  if (dateHit) {
    date = ymd(dateHit.date);
    hasExplicitDate = true;
  } else {
    // No date typed — keep the original useful behavior: count back from the
    // event using the category lead time so long-lead work gets a safe start.
    const base = eventDate || new Date(t.getTime() + 60 * DAY);
    const lss = lastSafeStart(base, lead);
    date = lss ? ymd(lss) : ymd(t);
  }

  return {
    title,
    detail: original,
    date,
    time: timeHit ? timeHit.time : null,
    hasExplicitDate,
    category,
    lead,
  };
}

export default parseCapture;
